<template>
  <dl v-if="organization && fieldAccess" class="m-0">
    <MnRow class="mb-3">
      <MnColumn cols="5">
        <ReadOnlyText :label="orgNameLabel" :value="organizationNameAndId" :showLoading="showLoading" labelUpperCase />
      </MnColumn>
      <MnColumn  v-if="!organization.isPrimary" cols="5">
        <ReadOnlyText label="Parent Organization" :value="organization.parentOrganizationName" :showLoading="showLoading" labelUpperCase />
      </MnColumn>      
      <MnColumn cols="2">
        <ReadOnlyText label="Status" :value="organization.status" :showLoading="showLoading" labelUpperCase>
          <template #content>
            <Tag :value="organization.status"
                 :severity="organization.status === 'Active' ? 'success' : 'danger'" 
                 class="px-5" rounded />
          </template>
        </ReadOnlyText>
      </MnColumn>
    </MnRow>
    <MnRow>
      <MnColumn v-if="isPhysicalAddressVisible" cols="6">
        <ReadOnlyText label="Physical Address" :value="fullAddress" :showLoading="showLoading" labelUpperCase />
      </MnColumn>
      <MnColumn v-if="isMailingAddressVisible" cols="6">
        <ReadOnlyText label="Mailing Address" :value="fullMailingAddress" :showLoading="showLoading" labelUpperCase />
      </MnColumn>
    </MnRow>
    <MnRow>
      <MnColumn cols="4">
        <ReadOnlyText label="Organization Type" :value="organization.organizationTypeDesc" :showLoading="showLoading" labelUpperCase />
      </MnColumn>
      <MnColumn cols="4">
        <div v-if="fieldAccess.npiNumber.visible">
          <ReadOnlyText label="NPI Number" :value="organization?.npiNumber" :showLoading="showLoading" labelUpperCase />
        </div>
        <div v-if="fieldAccess.naicNumber.visible">
          <ReadOnlyText label="NAIC Number" :value="organization?.naicNumber" :showLoading="showLoading" labelUpperCase />
        </div>
        <div v-if="fieldAccess.taxId.visible">
          <ReadOnlyText label="Tax ID" :value="organization?.taxId" :showLoading="showLoading" labelUpperCase />
        </div>  
      </MnColumn>
      <MnColumn cols="4" v-if="organization.webUrl" class="mt-2">
        <ReadOnlyText label="Web Site" :showLoading="showLoading" labelUpperCase >
          <template #content>
            <a href="#" @click="viewWebSite">{{ organization.webUrl }}</a>
          </template>
        </ReadOnlyText>
      </MnColumn>
    </MnRow>
    <MnRow>
      <MnColumn class="text-center">
        <hr />
        <PvButton @click="editDialogVisible = true" rounded size="medium" label="Edit Organization" icon="pi pi-pencil" />
      </MnColumn>
    </MnRow>
  </dl>
  <AddEditOrganizationDialog v-if="editDialogVisible"
                             v-model:visible="editDialogVisible"
                             :editOrganizationId="organization.organizationId"
                             :dialogCaption="'Edit Organization'"
                             :saveButtonCaption="'Save Organization'"
                             @refreshOrganization="handleRefreshOrganization" />
</template>
<script setup>
// ===============================
// IMPORTS
// ===============================
// Vue Core
import { ref, computed, onMounted } from 'vue';

// Components
import ReadOnlyText from '@/components/common/ReadOnlyText.vue';
import MnRow from '../common/MnRow.vue';
import MnColumn from '../common/MnColumn.vue';
import Tag from 'primevue/tag';

// Composables
import { usePageHelper } from '@/composables/pageHelper';
import { useFieldAccessState } from '@/composables/FieldAccessState';
import { mapOrganizationTypeToShort } from '@/utils/organizationUtils';
import { MODEL_TYPES, ORGANIZATION_TYPES } from '@/utils/constants';
import AddEditOrganizationDialog from './AddEditOrganizationDialog.vue';

// Stores


// Constants and Utils


// ===============================
// PROPS & EMITS
// ===============================
const props = defineProps({
  organization: {
    type: [Object, null],
    required: false,
    default: null
  }
});

const emit = defineEmits(['refreshOrganization']);
// ===============================
// COMPOSABLES & STORES
// ===============================
const pageHelper = usePageHelper();
const { showLoading } = pageHelper;
const { getAllFieldAccess, showPhysicalAddress, showMailingAddress } = useFieldAccessState();

// ===============================
// REACTIVE STATE
// ===============================
// const organization = ref(null);
const fieldAccess = ref(null);
const isPhysicalAddressVisible = ref(false);
const isMailingAddressVisible = ref(false);
const editDialogVisible = ref(false);

// ===============================
// CONSTANTS
// ===============================


// ===============================
// COMPUTED PROPERTIES
// ===============================
const orgNameLabel = computed(() => {
  return props.organization.organizationTypeDesc !== ORGANIZATION_TYPES.HCE && props.organization.organizationTypeDesc !== ORGANIZATION_TYPES.TPA && props.organization.organizationTypeDesc !== ORGANIZATION_TYPES.URO ? 
        'Organization Name (ID)'.toLocaleUpperCase() : 'Organization Name'.toLocaleUpperCase();
});

const organizationNameAndId = computed(() => {
  const showId = props.organization.organizationTypeDesc !== ORGANIZATION_TYPES.HCE && props.organization.organizationTypeDesc !== ORGANIZATION_TYPES.TPA && props.organization.organizationTypeDesc !== ORGANIZATION_TYPES.URO;
  return `${props.organization.name} ${showId ? `(${props.organization.hccisId})` : ''}`;
});

const fullAddress = computed(() => {
  const parts = [
    props.organization.address1,
    props.organization.address2,
    props.organization.city && props.organization.stateProvince && props.organization.postalCode 
      ? `${props.organization.city}, ${props.organization.stateProvince} ${props.organization.postalCode}` 
      : null,
    props.organization.countyId > 0 ? props.organization.countyName : props.organization.otherCountyName
  ].filter(part => part); // Remove empty/null/undefined values

  return parts.join('<br>') || '';
});

const fullMailingAddress = computed(() => {
  const parts = [
    props.organization.mailAddress1,
    props.organization.mailAddress2,
    props.organization.mailCity && props.organization.mailStateProvince && props.organization.mailPostalCode 
      ? `${props.organization.mailCity}, ${props.organization.mailStateProvince} ${props.organization.mailPostalCode}` 
      : null
  ].filter(part => part); // Remove empty/null/undefined values

  return parts.join('<br>') || '';
});

// ===============================
// METHODS
// ===============================
const setFieldAccess = () => {
  fieldAccess.value = getAllFieldAccess(MODEL_TYPES.Organization, mapOrganizationTypeToShort()[props.organization.organizationTypeDesc]);
};

const handleRefreshOrganization = () => {
  emit('refreshOrganization');
};

const initializeComponent = async () => {
  setFieldAccess();
  isPhysicalAddressVisible.value = showPhysicalAddress(mapOrganizationTypeToShort()[props.organization.organizationTypeDesc]);
  isMailingAddressVisible.value = showMailingAddress(mapOrganizationTypeToShort()[props.organization.organizationTypeDesc]);
  // await fetchTpaOptions();
};

// ===============================
// WATCHERS
// ===============================


// ===============================
// LIFECYCLE HOOKS
// ===============================
onMounted(async () => {
    pageHelper.fetch(() => initializeComponent());

});

// ===============================
// EXPOSE
// ===============================


</script>

<style scoped>

</style>